// src/components/garages/GarageDetail.jsx (continued)
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchGarageById,
  updateApprovalStatus,
  addReview,
  clearGarageError,
  clearGarageSuccess,
} from '../../store/actions/garageActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import { APPROVAL_STATUS } from '../../store/actions/actionTypes';

const GarageDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { currentGarage, loading, error, success } = useSelector(
    (state) => state.garages
  );

  const [activeTab, setActiveTab] = useState('info');
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewData, setReviewData] = useState({
    rating: 5,
    comment: '',
    reviewerName: '',
  });

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    backButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    actionButtons: {
      display: 'flex',
      gap: '0.5rem',
    },
    editButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#4f46e5',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    editButtonHover: {
      backgroundColor: '#4338ca',
    },
    approveButton: (status) => ({
      padding: '0.5rem 1rem',
      backgroundColor: status === 'APPROVED' ? '#d1fae5' : '#059669',
      color: status === 'APPROVED' ? '#065f46' : '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: status === 'APPROVED' ? 'default' : 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
    }),
    statusBadge: (status) => ({
      padding: '0.25rem 0.75rem',
      fontSize: '0.75rem',
      fontWeight: 600,
      borderRadius: '9999px',
      backgroundColor:
        status === 'APPROVED' ? '#d1fae5' :
        status === 'PENDING' ? '#fef3c7' :
        status === 'REJECTED' ? '#fee2e2' : '#f3f4f6',
      color:
        status === 'APPROVED' ? '#065f46' :
        status === 'PENDING' ? '#92400e' :
        status === 'REJECTED' ? '#991b1b' : '#374151',
    }),
    tabs: {
      display: 'flex',
      borderBottom: '2px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      marginBottom: '2rem',
    },
    tab: (active) => ({
      padding: '0.75rem 1.5rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: active ? '#2563eb' : '#6b7280',
      backgroundColor: active ? '#c9cbd0':'#f9fafb',
      borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginBottom: '-2px',
    }),
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '2rem',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '2rem',
    },
    section: {
      marginBottom: '2rem',
    },
    sectionTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#374151',
      marginBottom: '1rem',
      paddingBottom: '0.5rem',
      borderBottom: '1px solid #e5e7eb',
    },
    infoRow: {
      display: 'flex',
      marginBottom: '0.75rem',
    },
    infoLabel: {
      width: '120px',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
    },
    infoValue: {
      flex: 1,
      fontSize: '0.875rem',
      color: '#111827',
    },
    servicesContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    serviceItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.75rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.375rem',
    },
    serviceType: {
      padding: '0.25rem 0.5rem',
      backgroundColor: '#e0e7ff',
      color: '#3730a3',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
      fontWeight: 500,
    },
    serviceDescription: {
      fontSize: '0.875rem',
      color: '#4b5563',
    },
    servicePrice: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#059669',
    },
    reviewsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    reviewItem: {
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
    },
    reviewHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.5rem',
    },
    reviewerName: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#111827',
    },
    reviewDate: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    ratingStars: {
      display: 'flex',
      gap: '0.125rem',
      marginBottom: '0.5rem',
    },
    starFilled: {
      color: '#fbbf24',
      width: '1rem',
      height: '1rem',
    },
    starEmpty: {
      color: '#d1d5db',
      width: '1rem',
      height: '1rem',
    },
    reviewComment: {
      fontSize: '0.875rem',
      color: '#4b5563',
      lineHeight: '1.5',
    },
    verifiedBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.125rem 0.375rem',
      backgroundColor: '#dbeafe',
      color: '#1e40af',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
      marginLeft: '0.5rem',
    },
    addReviewButton: {
      marginTop: '1rem',
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#2563eb',
      border: '1px solid #2563eb',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
    },
    addReviewButtonHover: {
      backgroundColor: '#eff6ff',
    },
    reviewForm: {
      marginTop: '1rem',
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
    },
    ratingSelector: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '1rem',
    },
    starButton: (selected) => ({
      padding: '0.25rem',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: selected ? '#fbbf24' : '#d1d5db',
    }),
    submitReviewButton: {
      marginTop: '1rem',
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitReviewButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    input: {
      width: '100%',
      padding: '0.625rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      marginBottom: '0.75rem',
    },
    textarea: {
      width: '100%',
      padding: '0.625rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      minHeight: '80px',
      resize: 'vertical',
      marginBottom: '0.75rem',
    },
    emptyState: {
      textAlign: 'center',
      padding: '2rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    mapPlaceholder: {
      height: '300px',
      backgroundColor: '#f3f4f6',
      borderRadius: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#6b7280',
      fontSize: '0.875rem',
      border: '1px solid #e5e7eb',
    },
    coordinates: {
      fontSize: '0.875rem',
      color: '#059669',
      fontFamily: 'monospace',
    },
    specializationsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
    },
    specializationBadge: {
      padding: '0.25rem 0.5rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
    },
    deleteButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#dc2626',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    deleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    statsCard: {
      padding: '1.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      textAlign: 'center',
    },
    statsNumber: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#2563eb',
      marginBottom: '0.25rem',
    },
    statsLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchGarageById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    return () => {
      dispatch(clearGarageError());
      dispatch(clearGarageSuccess());
    };
  }, [dispatch]);

  const handleApproveClick = (status) => {
    setSelectedStatus(status);
    setShowApproveConfirm(true);
  };

  const handleApproveConfirm = async () => {
    try {
      await dispatch(updateApprovalStatus(id, selectedStatus));
      setShowApproveConfirm(false);
      setSelectedStatus(null);
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleCloseApprove = () => {
    setShowApproveConfirm(false);
    setSelectedStatus(null);
  };

  const handleAddReview = async () => {
    try {
      await dispatch(addReview(id, reviewData));
      setShowReviewForm(false);
      setReviewData({ rating: 5, comment: '', reviewerName: '' });
    } catch (error) {
      console.error('Failed to add review:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (i <= Math.floor(rating)) {
        stars.push(
          <svg key={i} style={styles.starFilled} fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        );
      } else {
        stars.push(
          <svg key={i} style={styles.starEmpty} fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        );
      }
    }
    return stars;
  };

  if (loading && !currentGarage) {
    return <LoadingSpinner fullScreen />;
  }

  if (!currentGarage && !loading) {
    return (
      <div style={styles.container}>
        <Alert type="error" message="Garage not found" />
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <Link to="/admin/garages" style={styles.backButton}>
            Retour aux garages
          </Link>
        </div>
      </div>
    );
  }

  const garage = currentGarage;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.titleContainer}>
          <Link
            to="/admin/garages"
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
            }}
          >
            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour aux garages
          </Link>
          <h1 style={styles.title}>{garage.name}</h1>
          <span style={styles.statusBadge(garage.approvalStatus)}>
            {garage.approvalStatus}
          </span>
        </div>

        <div style={styles.actionButtons}>
          <Link
            to={`/admin/garages/${id}/edit`}
            style={styles.editButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButton.backgroundColor;
            }}
          >
            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Modifier
          </Link>

          {garage.approvalStatus !== 'APPROVED' && (
            <button
              onClick={() => handleApproveClick('APPROVED')}
              style={styles.approveButton('PENDING')}
              onMouseEnter={(e) => {
                if (garage.approvalStatus !== 'APPROVED') {
                  e.currentTarget.style.backgroundColor = '#047857';
                }
              }}
              onMouseLeave={(e) => {
                if (garage.approvalStatus !== 'APPROVED') {
                  e.currentTarget.style.backgroundColor = '#059669';
                }
              }}
            >
              <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Approuver
            </button>
          )}

          {garage.approvalStatus === 'APPROVED' && (
            <button
              onClick={() => handleApproveClick('SUSPENDED')}
              style={styles.approveButton('APPROVED')}
            >
              <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              Suspendre
            </button>
          )}

          {garage.approvalStatus === 'SUSPENDED' && (
            <button
              onClick={() => handleApproveClick('APPROVED')}
              style={styles.approveButton('PENDING')}
            >
              <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Réactiver
            </button>
          )}

          <button
            onClick={() => handleApproveClick('REJECTED')}
            style={styles.deleteButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButton.backgroundColor;
            }}
          >
            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Rejeter
          </button>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearGarageError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearGarageSuccess())}
        />
      )}

      <div style={styles.tabs}>
        <button
          style={styles.tab(activeTab === 'info')}
          onClick={() => setActiveTab('info')}
        >
          Information
        </button>
        <button
          style={styles.tab(activeTab === 'services')}
          onClick={() => setActiveTab('services')}
        >
          Services
        </button>
        <button
          style={styles.tab(activeTab === 'reviews')}
          onClick={() => setActiveTab('reviews')}
        >
          Avis ({garage.reviews?.length || 0})
        </button>
        <button
          style={styles.tab(activeTab === 'location')}
          onClick={() => setActiveTab('location')}
        >
          Localisation
        </button>
      </div>

      <div style={styles.card}>
        {activeTab === 'info' && (
          <div style={styles.grid}>
            <div>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Informations de base</h3>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>SIRET:</span>
                  <span style={styles.infoValue}>{garage.siret}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Type:</span>
                  <span style={styles.infoValue}>
                    <span style={{ ...styles.serviceType, backgroundColor: '#f3f4f6', color: '#374151' }}>
                      {garage.type}
                    </span>
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Note:</span>
                  <span style={styles.infoValue}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={styles.ratingStars}>
                        {renderStars(garage.rating || 0)}
                      </div>
                      <span>({garage.rating ? garage.rating.toFixed(1) : '0.0'})</span>
                    </div>
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Total Réparations:</span>
                  <span style={styles.infoValue}>{garage.totalRepairs || 0}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Créé:</span>
                  <span style={styles.infoValue}>{formatDate(garage.createdAt)}</span>
                </div>
                {garage.approvedAt && (
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Approuvé:</span>
                    <span style={styles.infoValue}>{formatDate(garage.approvedAt)}</span>
                  </div>
                )}
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Informations de contact</h3>
                {garage.contact ? (
                  <>
                    {garage.contact.phone && (
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Téléphone:</span>
                        <span style={styles.infoValue}>
                          <a href={`tel:${garage.contact.phone}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                            {garage.contact.phone}
                          </a>
                        </span>
                      </div>
                    )}
                    {garage.contact.emergencyPhone && (
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Urgence:</span>
                        <span style={styles.infoValue}>
                          <a href={`tel:${garage.contact.emergencyPhone}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                            {garage.contact.emergencyPhone}
                          </a>
                        </span>
                      </div>
                    )}
                    {garage.contact.email && (
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Email:</span>
                        <span style={styles.infoValue}>
                          <a href={`mailto:${garage.contact.email}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                            {garage.contact.email}
                          </a>
                        </span>
                      </div>
                    )}
                    {garage.contact.website && (
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Website:</span>
                        <span style={styles.infoValue}>
                          <a href={garage.contact.website} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }}>
                            {garage.contact.website}
                          </a>
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={styles.emptyState}>Aucune information de contact disponible</div>
                )}
              </div>
            </div>

            <div>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Adresse</h3>
                {garage.address ? (
                  <>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Rue:</span>
                      <span style={styles.infoValue}>{garage.address.street}</span>
                    </div>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>City:</span>
                      <span style={styles.infoValue}>{garage.address.city}</span>
                    </div>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Code Postal:</span>
                      <span style={styles.infoValue}>{garage.address.postalCode}</span>
                    </div>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Pays:</span>
                      <span style={styles.infoValue}>{garage.address.country}</span>
                    </div>
                    {garage.address.latitude && garage.address.longitude && (
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Coordonnées:</span>
                        <span style={styles.coordinates}>
                          {garage.address.latitude}, {garage.address.longitude}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={styles.emptyState}>Aucune adresse disponible</div>
                )}
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Spécialisations</h3>
                {garage.specializations && garage.specializations.length > 0 ? (
                  <div style={styles.specializationsContainer}>
                    {garage.specializations.map((spec) => (
                      <span key={spec.make.id} style={styles.specializationBadge}>
                        {spec.make.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={styles.emptyState}>Aucune spécialisation disponible</div>
                )}
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Statistiques</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  <div style={styles.statsCard}>
                    <div style={styles.statsNumber}>{garage._count?.reviews || 0}</div>
                    <div style={styles.statsLabel}>Total Avis</div>
                  </div>
                  <div style={styles.statsCard}>
                    <div style={styles.statsNumber}>{garage._count?.services || 0}</div>
                    <div style={styles.statsLabel}>Services Offerts</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'services' && (
          <div>
            <h3 style={styles.sectionTitle}>Services Offerts</h3>
            {garage.services && garage.services.length > 0 ? (
              <div style={styles.servicesContainer}>
                {garage.services.map((service) => (
                  <div key={service.id} style={styles.serviceItem}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={styles.serviceType}>{service.type}</span>
                      {service.description && (
                        <span style={styles.serviceDescription}>{service.description}</span>
                      )}
                    </div>
                    {service.priceEstimate && (
                      <span style={styles.servicePrice}>€{service.priceEstimate}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={styles.emptyState}>Aucun service offert</div>
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={styles.sectionTitle}>Avis Clients</h3>
              <button
                onClick={() => setShowReviewForm(!showReviewForm)}
                style={styles.addReviewButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.addReviewButtonHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = styles.addReviewButton.backgroundColor;
                }}
              >
                <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Ajouter un Avis
              </button>
            </div>

            {showReviewForm && (
              <div style={styles.reviewForm}>
                <div style={styles.ratingSelector}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setReviewData({ ...reviewData, rating: star })}
                      style={styles.starButton(star <= reviewData.rating)}
                    >
                      <svg style={{ width: '2rem', height: '2rem' }} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Votre nom (optionnel)"
                  value={reviewData.reviewerName}
                  onChange={(e) => setReviewData({ ...reviewData, reviewerName: e.target.value })}
                  style={styles.input}
                />
                <textarea
                  placeholder="Écrivez votre avis..."
                  value={reviewData.comment}
                  onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                  style={styles.textarea}
                />
                <button
                  onClick={handleAddReview}
                  style={styles.submitReviewButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = styles.submitReviewButtonHover.backgroundColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = styles.submitReviewButton.backgroundColor;
                  }}
                >
                  Soumettre l'Avis
                </button>
              </div>
            )}

            {garage.reviews && garage.reviews.length > 0 ? (
              <div style={styles.reviewsContainer}>
                {garage.reviews.map((review) => (
                  <div key={review.id} style={styles.reviewItem}>
                    <div style={styles.reviewHeader}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={styles.reviewerName}>
                          {review.reviewerName || 'Anonyme'}
                        </span>
                        {review.verified && (
                          <span style={styles.verifiedBadge}>
                            <svg style={{ width: '0.75rem', height: '0.75rem' }} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Verifié
                          </span>
                        )}
                      </div>
                      <span style={styles.reviewDate}>{formatDate(review.createdAt)}</span>
                    </div>
                    <div style={styles.ratingStars}>
                      {renderStars(review.rating)}
                    </div>
                    {review.comment && (
                      <p style={styles.reviewComment}>{review.comment}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={styles.emptyState}>Aucun avis pour le moment</div>
            )}
          </div>
        )}

        {activeTab === 'location' && (
          <div>
            <h3 style={styles.sectionTitle}>Localisation du Garage</h3>
            {garage.address ? (
              <>
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Adresse:</span>
                    <span style={styles.infoValue}>
                      {garage.address.street}, {garage.address.postalCode} {garage.address.city}, {garage.address.country}
                    </span>
                  </div>
                  {garage.address.latitude && garage.address.longitude && (
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Coordonnées:</span>
                      <span style={styles.coordinates}>
                        {garage.address.latitude}, {garage.address.longitude}
                      </span>
                    </div>
                  )}
                </div>
                <div style={styles.mapPlaceholder}>
                  {garage.address.latitude && garage.address.longitude ? (
                    <div style={{ textAlign: 'center' }}>
                      <p>La carte s'affiche ici avec les coordonnées:</p>
                      <p style={styles.coordinates}>
                        {garage.address.latitude}, {garage.address.longitude}
                      </p>
                      <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                        (Google Maps or OpenStreetMap integration)
                      </p>
                    </div>
                  ) : (
                    <p>Aucune coordonnée disponible pour cette adresse</p>
                  )}
                </div>
              </>
            ) : (
              <div style={styles.emptyState}>Aucune information d'adresse disponible</div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={showApproveConfirm}
        onClose={handleCloseApprove}
        onConfirm={handleApproveConfirm}
        title={`${selectedStatus === 'APPROVED' ? 'Approuver' : selectedStatus === 'SUSPENDED' ? 'Suspendre' : 'Rejeter'} Garage`}
        message={`Êtes-vous sûr de vouloir ${selectedStatus === 'APPROVED' ? 'approuver' : selectedStatus === 'SUSPENDED' ? 'suspendre' : 'rejeter'} "${garage?.name}"?`}
        confirmText={selectedStatus === 'APPROVED' ? 'Approuver' : selectedStatus === 'SUSPENDED' ? 'Suspendre' : 'Rejeter'}
        cancelText="Annuler"
        type={selectedStatus === 'APPROVED' ? 'success' : selectedStatus === 'SUSPENDED' ? 'warning' : 'danger'}
      />
    </div>
  );
};

export default GarageDetail;